import { Component, OnInit, Input, ViewChild, ElementRef, AfterViewInit, HostBinding } from '@angular/core';
import { NbDialogRef } from '@nebular/theme';
import { CtaOnImageResponse, GeneratorService } from 'src/app/deebr/service/generator.service';

type ResizeDirection = 'nw' | 'ne' | 'sw' | 'se' | null;

export interface CtaOption {
  style: string;
  label: string;
  url: string;
}

/** Variantes de forme renvoyées par le service CTA, dans l'ordre d'affichage. */
export function buildCtaOptions(response: CtaOnImageResponse): CtaOption[] {
  return [
    { style: 'solid', label: 'Rectangle', url: response.cta_image_solid },
    { style: 'rounded', label: 'Arrondi', url: response.cta_image_rounded },
    { style: 'outline', label: 'Contour', url: response.cta_image_outline },
  ].filter(option => !!option.url);
}

/**
 * Placement du bouton CTA sur un visuel.
 *
 * Meme principe que WriteOnImageComponent : le service ne renvoie que le CTA en
 * PNG transparent (haute definition), l'utilisateur le deplace / redimensionne
 * ici, et la fusion finale est faite cote back par merge_image_overlay.
 */
@Component({
  selector: 'app-write-cta-on-image',
  templateUrl: './write-cta-on-image.component.html',
  styleUrls: ['./write-cta-on-image.component.scss'],
  standalone: false
})
export class WriteCtaOnImageComponent implements OnInit, AfterViewInit {
  /** Largeur initiale du CTA, en part de la largeur du visuel. */
  private static readonly INITIAL_WIDTH_RATIO = 0.45;
  /** Marge basse initiale, en part de la hauteur du visuel. */
  private static readonly INITIAL_BOTTOM_RATIO = 0.12;
  private static readonly MIN_WIDTH_PX = 30;
  /**
   * Hauteur reservee au titre, aux vignettes et aux boutons de la modale : le
   * visuel doit tenir dans ce qui reste, sinon le footer passe sous l'ecran.
   */
  private static readonly VERTICAL_CHROME_PX = 300;

  @Input() staticImageUrl = '';
  @Input() ctaOptions: CtaOption[] = [];
  @HostBinding('class.loaded') isLoaded = false;

  @ViewChild('overlayContainer', { static: false }) overlayContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('overlayImg', { static: false }) overlayImg!: ElementRef<HTMLImageElement>;
  @ViewChild('staticImgEl', { static: false }) staticImgEl!: ElementRef<HTMLImageElement>;

  isLoading = true;
  loading = false;
  selectedOption: CtaOption | null = null;
  currentOverlayUrl = '';

  isDragging = false;
  isResizing = false;
  resizeDirection: ResizeDirection = null;

  startX = 0;
  startY = 0;
  currentX = 0;
  currentY = 0;
  startWidth = 0;
  startHeight = 0;
  currentWidth = 0;
  currentHeight = 0;
  resizeStartX = 0;
  resizeStartY = 0;
  scaleFactor = 1;
  private placementDone = false;

  constructor(
    protected dialogRef: NbDialogRef<WriteCtaOnImageComponent>,
    private generatorService: GeneratorService
  ) {}

  ngOnInit(): void {
    setTimeout(() => {
      this.isLoaded = true;
    });
  }

  ngAfterViewInit(): void {
    if (this.staticImgEl?.nativeElement) {
      this.staticImgEl.nativeElement.onload = () => this.onStaticImageLoad();
    }

    document.addEventListener('mouseup', () => this.onMouseUp());
    document.addEventListener('mousemove', (e) => this.onMouseMove(e));
  }

  /** Alimente la modale une fois la reponse du back recue. */
  setCtaOptions(staticImageUrl: string, options: CtaOption[]): void {
    if (!options.length) {
      return;
    }
    this.staticImageUrl = staticImageUrl;
    this.ctaOptions = options;
    this.selectedOption = options[0];
    this.currentOverlayUrl = options[0].url;
    this.isLoading = false;
  }

  onStaticImageLoad(): void {
    const container = this.overlayContainer?.nativeElement;
    const staticImg = this.staticImgEl?.nativeElement;
    if (!container || !staticImg?.naturalWidth) return;

    const maxHeight = Math.max(240, window.innerHeight - WriteCtaOnImageComponent.VERTICAL_CHROME_PX);
    const maxWidth = window.innerWidth * 0.75;
    const imgRatio = staticImg.naturalWidth / staticImg.naturalHeight;

    let adjustedWidth = staticImg.naturalWidth;
    let adjustedHeight = staticImg.naturalHeight;

    if (adjustedHeight > maxHeight) {
      adjustedHeight = maxHeight;
      adjustedWidth = adjustedHeight * imgRatio;
    }
    if (adjustedWidth > maxWidth) {
      adjustedWidth = maxWidth;
      adjustedHeight = adjustedWidth / imgRatio;
    }

    this.scaleFactor = adjustedWidth / staticImg.naturalWidth;

    staticImg.style.width = `${adjustedWidth}px`;
    staticImg.style.height = `${adjustedHeight}px`;
    container.style.width = `${adjustedWidth}px`;
    container.style.height = `${adjustedHeight}px`;

    this.placeCta();
  }

  onOverlayLoad(): void {
    this.placeCta();
  }

  /**
   * Position de depart : CTA centre en bas du visuel, a une largeur relative.
   * Le PNG genere est bien plus grand que le visuel, on ne peut donc pas partir
   * de sa taille naturelle comme le fait write-on-image.
   */
  private placeCta(): void {
    if (this.placementDone) return;

    const container = this.overlayContainer?.nativeElement;
    const overlay = this.overlayImg?.nativeElement;
    if (!container?.clientWidth || !overlay?.naturalWidth) return;

    const aspectRatio = overlay.naturalWidth / overlay.naturalHeight;

    this.currentWidth = Math.round(container.clientWidth * WriteCtaOnImageComponent.INITIAL_WIDTH_RATIO);
    this.currentHeight = Math.round(this.currentWidth / aspectRatio);
    this.currentX = Math.round((container.clientWidth - this.currentWidth) / 2);
    this.currentY = Math.round(
      container.clientHeight
      - this.currentHeight
      - container.clientHeight * WriteCtaOnImageComponent.INITIAL_BOTTOM_RATIO
    );

    this.placementDone = true;
    this.updateOverlayStyles();
  }

  selectOption(option: CtaOption): void {
    if (this.isLoading) return;
    // Toutes les variantes ont les memes dimensions : position et taille sont
    // conservees quand on change de forme.
    this.selectedOption = option;
    this.currentOverlayUrl = option.url;
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

  confirm(): void {
    if (!this.selectedOption) {
      this.dialogRef.close(null);
      return;
    }

    this.loading = true;

    const scaledX = Math.round(this.currentX / this.scaleFactor);
    const scaledY = Math.round(this.currentY / this.scaleFactor);
    const scaledWidth = Math.round(this.currentWidth / this.scaleFactor);
    const scaledHeight = Math.round(this.currentHeight / this.scaleFactor);

    this.generatorService.mergeImages(
      this.staticImageUrl,
      this.selectedOption.url,
      scaledX,
      scaledY,
      scaledWidth,
      scaledHeight
    ).subscribe(
      (response) => {
        this.loading = false;
        const mergedImageUrl = typeof response === 'string' ? response : response?.merged_image_url;

        if (mergedImageUrl) {
          this.dialogRef.close({ mergedImageUrl });
        } else {
          console.error('Format de reponse inattendu pour la fusion du CTA :', response);
          this.dialogRef.close({ mergedImageUrl: undefined });
        }
      },
      (error) => {
        this.loading = false;
        console.error('Erreur lors de la fusion du CTA :', error);
        this.dialogRef.close(undefined);
      }
    );
  }

  onImageMouseDown(event: MouseEvent): void {
    if (this.isResizing) return;
    this.isDragging = true;
    this.startX = event.clientX - this.currentX;
    this.startY = event.clientY - this.currentY;
    event.preventDefault();
    event.stopPropagation();
  }

  onResizeHandleDown(event: MouseEvent, direction: ResizeDirection): void {
    this.isResizing = true;
    this.resizeDirection = direction;
    this.startWidth = this.currentWidth;
    this.startHeight = this.currentHeight;
    this.resizeStartX = event.clientX;
    this.resizeStartY = event.clientY;
    event.preventDefault();
    event.stopPropagation();
  }

  onMouseUp(): void {
    this.isDragging = false;
    this.isResizing = false;
    this.resizeDirection = null;
  }

  onMouseMove(event: MouseEvent): void {
    const container = this.overlayContainer?.nativeElement;
    if (!container) return;

    if (this.isDragging && !this.isResizing) {
      // Un CTA qui deborde du visuel serait tronque a la fusion : on garde le
      // bouton entierement dans l'image.
      this.currentX = this.clamp(event.clientX - this.startX, container.clientWidth - this.currentWidth);
      this.currentY = this.clamp(event.clientY - this.startY, container.clientHeight - this.currentHeight);
      this.updateOverlayStyles();
      return;
    }

    if (this.isResizing && this.resizeDirection) {
      const deltaX = event.clientX - this.resizeStartX;
      const aspectRatio = this.startWidth / this.startHeight;
      const growing = this.resizeDirection === 'ne' || this.resizeDirection === 'se';

      let width = growing ? this.startWidth + deltaX : this.startWidth - deltaX;
      width = Math.max(WriteCtaOnImageComponent.MIN_WIDTH_PX, Math.min(width, container.clientWidth));
      const height = width / aspectRatio;

      // Les poignees ouest / nord tirent le bouton : le coin oppose reste fixe.
      if (this.resizeDirection === 'nw' || this.resizeDirection === 'sw') {
        this.currentX += this.currentWidth - width;
      }
      if (this.resizeDirection === 'nw' || this.resizeDirection === 'ne') {
        this.currentY += this.currentHeight - height;
      }

      this.currentWidth = Math.round(width);
      this.currentHeight = Math.round(height);
      this.currentX = Math.round(this.clamp(this.currentX, container.clientWidth - this.currentWidth));
      this.currentY = Math.round(this.clamp(this.currentY, container.clientHeight - this.currentHeight));

      this.updateOverlayStyles();
    }
  }

  private clamp(value: number, max: number): number {
    return Math.max(Math.min(0, max), Math.min(value, Math.max(0, max)));
  }

  updateOverlayStyles(): void {
    if (!this.overlayImg) return;
    const overlayWrapper = this.overlayImg.nativeElement.parentElement as HTMLElement;
    overlayWrapper.style.transform = `translate(${this.currentX}px, ${this.currentY}px)`;
    overlayWrapper.style.width = `${this.currentWidth}px`;
    overlayWrapper.style.height = `${this.currentHeight}px`;
  }
}
